import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Project } from "@overmap-ai/common"
import { selectProjectsOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useProjectTable, useProjectTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { projectFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/projects/")

export const ProjectSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const projects = useShallowEqaulSelector(selectProjectsOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(project: Project) => {
			void navigate({
				from: "/$organizationId/settings/projects/",
				to: "/$organizationId/settings/projects/$projectId",
				params: (prev) => ({ ...prev, projectId: project.uuid }),
			})
		},
		[navigate],
	)

	const [filteredProjects, filterValue, setFilterValue] = useTextFilter(projects, projectFilterCallback)

	const columns = useProjectTableColumns()

	const table = useProjectTable({
		data: filteredProjects,
		columns: columns,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Projects</Heading>
					<Text size="sm" accentColor="base">
						Manage the projects of your organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 grow flex-col gap-2">
					<div className="flex w-full justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link from="/$organizationId/settings/projects/" to="/$organizationId/settings/projects/create">
							<Button accentColor="primary" variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							size="sm"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleRowClick}
							interactive
							emptyText="No projects."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
ProjectSettingsView.displayName = "ProjectSettingsView"
